/**
 * The redline list: every open finding with its CITATION (the code
 * section is the authority), measured-vs-required, and the suggestion
 * -- appliable kinds get one-click Accept fix (applies the exact
 * correction); advisory kinds are informational and Accept merely
 * stands as the approval (never the apply path). The header
 * carries the run's ROLLUP: in-compliance is a first-class claim,
 * not the absence of red. Clicking a finding selects its element.
 */
import { ACCENT, INK, tint } from '../../../theme';
import { ComplianceTag, isAppliable, RunRollup, SuggestionNote, VERDICT } from '../../../strata';
import type { StoredFinding } from '../redline';
import type { RunReportTotals } from '../redline';

const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0));

// The ruleset's citations, keyed by name -- mirrors the server
// ruleset for display (the server remains the authority on content).
const RULE_CITATIONS: Record<string, string> = {
  'ipc-704-1-sanitary-slope': 'IPC 2021 §704.1',
  'imc-307-2-2-condensate-slope': 'IMC 2021 §307.2.2',
  'nec-240-24-a-device-height': 'NEC 2023 §240.24(A)',
};

const actionButton = (paint: string, filled = true) => ({
  fontSize: '12px',
  padding: '0.2rem 0.6rem',
  borderRadius: '4px',
  border: `1px solid ${paint}`,
  background: filled ? tint(paint, 0.15) : 'transparent',
  color: paint,
  cursor: 'pointer',
});

export function FindingsPanel({
  findings,
  report,
  filtered = false,
  selectedOriginId,
  busy,
  exporting,
  onSelect,
  onAccept,
  onReject,
  onFixAll,
  onExport,
}: {
  findings: StoredFinding[];
  // The latest run's counts (null while loading / no run yet): the
  // affirmative claim rides the panel header.
  report: RunReportTotals | null;
  // An active filter narrows this panel's findings AND the fix-all
  // scope -- the button says so (the aggregate verb names its set).
  filtered?: boolean;
  selectedOriginId: string | null;
  busy: boolean;
  exporting: boolean;
  onSelect: (originId: string) => void;
  onAccept: (findingId: number, apply: boolean) => void;
  onReject: (findingId: number) => void;
  onFixAll: () => void;
  onExport: () => void;
}) {
  const exportButton = (
    <button disabled={busy || exporting} onClick={onExport} style={actionButton(ACCENT.blue)}>
      {exporting ? 'Exporting…' : 'Export IFC'}
    </button>
  );
  const rollup = report && (
    <div style={{ marginTop: '0.5rem' }}>
      <RunRollup
        compliant={report.compliant}
        breach={report.breach}
      />
    </div>
  );
  const open = findings.filter((f) => f.state === 'open');
  if (open.length === 0) {
    return (
      <div className="ui-overlay glass-panel" style={{ top: '1rem', left: '1rem', width: '300px', padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary }}>
            Redlines
          </span>
          {exportButton}
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <ComplianceTag state="complying">✓ No open findings</ComplianceTag>
        </div>
        {rollup}
      </div>
    );
  }
  return (
    <div
      className="ui-overlay glass-panel"
      style={{ top: '1rem', left: '1rem', width: '320px', maxHeight: '70vh', overflowY: 'auto', padding: '0.75rem 1rem' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary }}>
          Redlines ({open.length})
        </span>
        <span style={{ display: 'flex', gap: '0.4rem' }}>
          <button disabled={busy} onClick={onFixAll} style={actionButton(VERDICT.complying)}>
            {busy ? 'Fixing…' : filtered ? `Fix all · ${open.length} filtered` : 'Fix all'}
          </button>
          {exportButton}
        </span>
      </div>
      {rollup && <div style={{ marginBottom: '0.5rem', marginTop: '-0.25rem' }}>{rollup}</div>}
      {open.map((finding) => (
        <div
          key={finding.id}
          onClick={() => onSelect(finding.origin_id)}
          style={{
            borderLeft: `3px solid ${
              finding.origin_id === selectedOriginId ? VERDICT.violating : tint(VERDICT.violating, 0.35)
            }`,
            background:
              finding.origin_id === selectedOriginId ? tint(VERDICT.violating, 0.08) : 'transparent',
            padding: '0.5rem 0.6rem',
            marginBottom: '0.5rem',
            cursor: 'pointer',
            borderRadius: '4px',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 600, color: VERDICT.violating }}>
            {RULE_CITATIONS[finding.rule_name] ?? finding.rule_name}
          </div>
          <div style={{ fontSize: '12px', color: INK.primary, margin: '0.15rem 0', wordBreak: 'break-all' }}>
            {finding.element_name ?? finding.origin_id}
          </div>
          {'slope_pct' in finding.measured && (
            <div style={{ fontSize: '12px', color: INK.secondary }}>
              slope {num(finding.measured.slope_pct).toFixed(2)}% · required ≥{' '}
              {num(finding.required.min_slope_pct).toFixed(2)}%
            </div>
          )}
          {finding.suggestion && (
            <SuggestionNote
              kind={finding.suggestion.kind}
              description={finding.suggestion.description}
            />
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
            {finding.suggestion && (
              <button
                disabled={busy}
                aria-label={`${isAppliable(finding.suggestion.kind) ? 'Accept fix' : 'Accept'} — ${
                  finding.element_name ?? finding.origin_id
                }, ${RULE_CITATIONS[finding.rule_name] ?? finding.rule_name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(finding.id, isAppliable(finding.suggestion!.kind));
                }}
                style={actionButton(VERDICT.complying)}
              >
                {isAppliable(finding.suggestion.kind) ? 'Accept fix' : 'Accept'}
              </button>
            )}
            <button
              disabled={busy}
              aria-label={`Reject — ${finding.element_name ?? finding.origin_id}, ${
                RULE_CITATIONS[finding.rule_name] ?? finding.rule_name
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onReject(finding.id);
              }}
              style={{ ...actionButton(INK.muted, false), color: INK.secondary }}
            >
              Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
